/**
 * TypedHandle - A Handle component that displays type-based colors
 *
 * Port types are visually distinguished by color to help
 * users understand what connections are valid.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Handle, HandleProps, Position } from 'reactflow'
import { useHideConnectionHandles } from './CanvasEdgesContext'
import { useNodePresentation } from './NodePresentationContext'
import { useHandlesVisibleForNode } from './HandleVisibilityContext'
import { getPortTypeColor, localizePortTypeLabel, getPortTypeHexColor, PortType } from './portTypes'

interface TypedHandleProps extends Omit<HandleProps, 'style'> {
  /** Port type for color styling */
  portType: PortType | string
  /** Override the default size (in pixels) */
  size?: number
  /** Whether to show a tooltip with type info */
  showTooltip?: boolean
  /** Custom style overrides */
  style?: React.CSSProperties
  /**
   * Owning node id. When provided, this handle is gated by per-node active-only
   * visibility (Change B). When omitted, it falls back to the canvas-wide
   * `alwaysHideEdges` master override only.
   */
  nodeId?: string
}

/**
 * Handle sizes by context
 */
export const DEFAULT_SIZE = 12
export const MINI_SIZE = 8

export const TypedHandle = memo(function TypedHandle({
  portType,
  size = DEFAULT_SIZE,
  showTooltip = true,
  style,
  nodeId,
  ...handleProps
}: TypedHandleProps) {
  const hideConnectionHandles = useHideConnectionHandles()
  const presentation = useNodePresentation()
  const { t } = useTranslation('canvas')
  // Per-node active-only gating when nodeId is supplied; the store already folds
  // `alwaysHideEdges` into its hide-all state, so this also covers the master
  // override. Called unconditionally (hook rules) with a stable fallback key.
  const perNodeHandlesVisible = useHandlesVisibleForNode(nodeId ?? '')
  const hexColor = getPortTypeHexColor(portType)
  const typeLabel = localizePortTypeLabel(portType, t)

  const handlesVisible =
    nodeId != null ? perNodeHandlesVisible : !hideConnectionHandles
  if (!handlesVisible || presentation === 'list' || presentation === 'detail') return null

  // Position-based offset to avoid overlapping node border
  const isLeft = handleProps.position === Position.Left
  const isRight = handleProps.position === Position.Right

  const handleStyle: React.CSSProperties = {
    width: size,
    height: size,
    background: hexColor,
    border: '2px solid rgba(255,255,255,0.3)',
    ...(isLeft && { left: -(size / 2 + 1) }),
    ...(isRight && { right: -(size / 2 + 1) }),
    ...style,
  }

  return (
    <Handle
      {...handleProps}
      style={handleStyle}
      title={showTooltip ? `${handleProps.id} (${typeLabel})` : undefined}
    />
  )
})

/**
 * Inline port indicator for showing port type in node UI.
 * Purely visual, not a React Flow handle.
 */
interface PortIndicatorProps {
  portType: PortType | string
  label?: string
  className?: string
}

export const PortIndicator = memo(function PortIndicator({ portType, label, className = '' }: PortIndicatorProps) {
  const { t } = useTranslation('canvas')
  const colors = getPortTypeColor(portType)
  const typeLabel = localizePortTypeLabel(portType, t)

  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xxs ${colors.bg} ${colors.text} ${className}`}
      title={typeLabel}
    >
      <span
        className={`w-2 h-2 rounded-full ${colors.border} border`}
        style={{ backgroundColor: getPortTypeHexColor(portType) }}
      />
      {label && <span>{label}</span>}
    </div>
  )
})

export { getPortTypeHexColor }
